import { createAsyncThunk, createSlice } from '@reduxjs/toolkit';
import { AxiosRequestConfig } from 'axios';

import api from '../../../api/axios';
import { IApiConfig, IApiResponseError, IApiState } from '../../../types/api';
import { RootState } from '../..';

type TStatus = 'approved' | 'rejected' | 'pending' | 'processing';

interface IGetKybResponse {
  success?: boolean;
  message?: string;
  data?: {
    bvnVerificationStatus?: TStatus;
    idVerificationStatus?: TStatus;
    ninVerificationStatus?: TStatus;
    nationalityVerificationStatus?: TStatus;
    cacNumber?: string;
    tinNumber?: string;
    businessId?: string;
    businessType?: 'REGISTERED' | 'SOLETRADER';
    documents?: [];
    isCompleted?: boolean;
    isRepresentativeApproved?: boolean;
    hasCdd?: boolean;
    addressVerificationStatus?: TStatus;
  };
  errors?: IApiResponseError[];
}

const initialState: IApiState<IGetKybResponse> = {
  value: null,
  status: 'idle',
  error: '',
};

export const getKybByIdAsync = createAsyncThunk(
  'kyb',
  async (_, { rejectWithValue, getState }) => {
    try {
      const state = getState() as RootState;

      const accessToken = state.auth.value?.data?.token?.accessToken;

      const businessId = localStorage.getItem('businessId');

      const config: IApiConfig = {
        headers: {
          Authorization: `Token ${accessToken}`,
        },
      };

      if (businessId) {
        config.headers['X-Business-Id'] = businessId;
      }

      const { data } = await api.get(
        !businessId
          ? 'kyb/overview/step-overview'
          : `kyb/overview/step-overview?businessId=${businessId}`,
        config as AxiosRequestConfig<IApiConfig>
      );

      return data;
    } catch (err) {
      return rejectWithValue(err);
    }
  }
);

export const kybGetByIdSlice = createSlice({
  name: 'kyb/getById',
  initialState,
  reducers: {
    resetGetKybById: (state) => {
      state.value = null;
      state.status = 'idle';
      state.error = '';
    },
  },
  extraReducers: (builder) => {
    builder
      .addCase(getKybByIdAsync.pending, (state) => {
        state.status = 'loading';
        state.error = '';
      })
      .addCase(getKybByIdAsync.fulfilled, (state, action) => {
        state.status = 'success';
        state.value = action.payload as IGetKybResponse;
      })
      .addCase(getKybByIdAsync.rejected, (state, action) => {
        state.status = 'failed';
        state.value = action.payload as IGetKybResponse;
        state.error = action.error.message;
      });
  },
});

export const { resetGetKybById } = kybGetByIdSlice.actions;

export default kybGetByIdSlice.reducer;
